import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase_client";
import * as XLSX from 'xlsx';
import '../../styles/page-base.css';

interface Customer {
  id: string;
  company_name: string;
  email: string;
  phone_number: string;
  created_at: string;
  credit_balance: number;
  currency_balance: number;
  referred_by: string;
  last_filing: string;
  last_filing_id: string;
  country_iso: string;
  currency: string;
}

export default function AdminCustomerList() {
    const [searchTerm, setSearchTerm] = useState("");
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();
    const [selectedCountry, setSelectedCountry] = useState<string>('MY');
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [showPreview, setShowPreview] = useState(false);
    const [defaultPassword, setDefaultPassword] = useState('Trial123!');

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch in batches to bypass 1000-row limit
      const BATCH_SIZE = 1000;
      let allCustomers: any[] = [];
      let hasMore = true;
      let offset = 0;

      while (hasMore) {
        const { data: batchCustomers, error } = await supabase
          .rpc('get_admin_customer_list')
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) {
          console.error('Error fetching customers:', error);
          setCustomers([]);
          return;
        }

        if (batchCustomers && batchCustomers.length > 0) {
          allCustomers = [...allCustomers, ...batchCustomers];
          offset += BATCH_SIZE;
          hasMore = batchCustomers.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      console.log(`Fetched ${allCustomers.length} customers in total`);

      // Transform to match your interface
      const customersData = allCustomers.map((customer: any) => ({
        id: customer.org_id,
        company_name: customer.company_name,
        email: customer.email || '-',
        phone_number: customer.phone_number || '-',
        created_at: customer.created_at,
        credit_balance: Number(customer.credit_balance) || 0,
        currency_balance: Number(customer.currency_balance) || 0,
        referred_by: customer.referred_by || '',
        last_filing: customer.last_filing || '',
        last_filing_id: customer.last_filing_id || '',
        country_iso: customer.country_iso,
        currency: customer.currency || 'MYR'
      }));

      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchCustomers();
    }, [fetchCustomers]);

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone_number.includes(searchTerm);
    
    // Filter by selected country tab (MY or SG)
    const matchesCountry = customer.country_iso === selectedCountry;
    
    return matchesSearch && matchesCountry;
  });

  const handleViewFilings = (orgId: string) => {
    navigate(`/admin/organizations/${orgId}/filings`);
  };

  const handleViewUsage = (orgId: string) => {
    navigate(`/admin/organizations/${orgId}/usage`);
  };

  const handleEdit = (orgId: string) => {
    navigate(`/admin/organizations/${orgId}`);
  };

  const handleExportExcel = () => {
    const headers = [
      'Company Name',
      'Email',
      'Phone Number',
      'Created At',
      'Credit Balance',
      'Referred By',
      'Last Filing'
    ];
  
    const csvData = [
      headers.join(','),
      ...filteredCustomers.map(customer => [
        `"${customer.company_name}"`,
        `"${customer.email}"`,
        `"${customer.phone_number
          ? `\t${customer.phone_number}` // Tab prefix to force Excel to treat as text
          : ''}"`,
        `"${customer.created_at}"`,
        `"${customer.credit_balance || '0'}"`,
        `"${customer.referred_by || '-'}"`,
        `"${customer.last_filing || '-'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
       const url = URL.createObjectURL(blob);
       const link = document.createElement('a');
       link.href = url;
    link.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
       document.body.appendChild(link);
       link.click();
       document.body.removeChild(link);
       URL.revokeObjectURL(url);
   };

  const handleAddCustomer = () => {
    setShowAddCustomerModal(true);
    setExcelFile(null);
    setUploadError(null);
    setUploadSuccess(null);
    setPreviewData([]);
    setShowPreview(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        if (!file.name.match(/\.(xlsx|xls)$/i)) {
            setUploadError('Please select an Excel file (.xlsx or .xls)');
            setExcelFile(null);
            return;
        }
        setExcelFile(file);
        setUploadError(null);
        setUploadSuccess(null);
        setPreviewData([]);
        setShowPreview(false);
    }
  };

  const handlePreview = async () => {
    if (!excelFile) {
        setUploadError('Please select an Excel file first');
        return;
    }

    if (!defaultPassword || defaultPassword.length < 6) {
        setUploadError('Default password must be at least 6 characters');
        return;
    }

    try {
        setUploadError(null);
        const arrayBuffer = await excelFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (data.length < 2) {
            setUploadError('Excel file must have at least a header row and one data row');
            return;
        }

        const headers = (data[0] as any[]).map((h: any) => String(h || '').toLowerCase().trim());
        
        const expectedColumns = ['company_name', 'email', 'phone_number', 'country_iso', 'company_address'];
        const missingColumns = expectedColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
            setUploadError(`Missing required columns: ${missingColumns.join(', ')}. Found columns: ${headers.join(', ')}`);
            return;
        }

        const parsedData = [];
        const validationErrors: string[] = [];
        
        for (let i = 1; i < data.length; i++) {
            const row = data[i] as any[];
            if (!row || row.length === 0) continue;

            const rowData: any = {};
            headers.forEach((header, index) => {
                rowData[header] = row[index] || '';
            });

            // Convert to string, strip invisible chars, then trim - handles string/numeric values
            const company_name = sanitizeCell(rowData.company_name);
            const contact_email_raw = sanitizeCell(rowData.email);
            const contact_phone_raw = sanitizeCell(rowData.phone_number);
            const billing_address = sanitizeCell(rowData.company_address);
            
            // Normalize email to lowercase
            const contact_email = contact_email_raw.toLowerCase();
            
            // Validate required fields
            if (!company_name || !contact_email || !contact_phone_raw) {
                validationErrors.push(`Row ${i + 1}: Skipping incomplete record: ${company_name || contact_email}`);
                continue;
            }
            
            // Basic email validation
            if (!validateEmail(contact_email)) {
                validationErrors.push(`Row ${i + 1}: Invalid email format: ${contact_email}`);
                continue;
            }
            
            // Standardize phone number
            const standardizedPhone = standardizePhoneNumber(contact_phone_raw);
            
            // Validate phone number format
            if (!validatePhoneNumber(standardizedPhone)) {
                validationErrors.push(`Row ${i + 1}: Invalid phone format '${contact_phone_raw}' → '${standardizedPhone}' for ${contact_email}`);
                continue;
            }

            parsedData.push({
                company_name: company_name,
                email: contact_email,
                phone_number: standardizedPhone,
                country_iso: sanitizeCell(rowData.country_iso).toUpperCase(),
                company_address: billing_address,
            });
        }

        if (validationErrors.length > 0) {
            const errorMessage = `Validation errors found:\n${validationErrors.slice(0, 10).join('\n')}${validationErrors.length > 10 ? `\n... and ${validationErrors.length - 10} more errors` : ''}`;
            setUploadError(errorMessage);
            if (parsedData.length === 0) {
                return; // Don't show preview if all rows have errors
            }
        }

        if (parsedData.length === 0) {
            setUploadError('No valid data rows found. Please ensure rows have valid company_name, email, and phone_number.');
            return;
        }

        setPreviewData(parsedData);
        setShowPreview(true);
        const successMessage = validationErrors.length > 0 
            ? `Found ${parsedData.length} valid customer(s) to import (${validationErrors.length} row(s) skipped due to errors)`
            : `Found ${parsedData.length} valid customer(s) to import`;
        setUploadSuccess(successMessage);
    } catch (error: any) {
        console.error('Error reading Excel file:', error);
        setUploadError(`Error reading Excel file: ${error.message}`);
    }
  };

  const handleImport = async () => {
    if (!excelFile || previewData.length === 0) {
        setUploadError('Please preview the file first');
        return;
    }

    if (!defaultPassword || defaultPassword.length < 6) {
        setUploadError('Default password must be at least 6 characters');
        return;
    }

    try {
        setUploading(true);
        setUploadError(null);
        setUploadSuccess(null);

        const { data, error } = await supabase.functions.invoke('import-customer', {
            body: {
                customers: previewData,
                default_password: defaultPassword
            }
        });

        if (error) {
            throw error;
        }

        if (data?.error) {
            throw new Error(data.error);
        }

        // Check if import actually succeeded
        const importedCount = data?.imported_count || 0;
        const skippedCount = data?.skipped_count || 0;
        const errors = data?.errors || [];
        const totalProcessed = importedCount + skippedCount;

        if (importedCount === 0) {
          // No customers were imported
          if (errors.length > 0) {
              // Format errors as structured data for better display
              const errorData = {
                  title: 'Import Failed',
                  message: 'No customers were imported.',
                  errors: errors
              };
              setUploadError(JSON.stringify(errorData));
              return; // Don't throw, just set error
          } else {
              // All were skipped (shouldn't happen, but handle it)
              setUploadError(`No customers were imported. All ${skippedCount} customer(s) were skipped.`);
              return;
          }
      } else if (errors.length > 0) {
          // Some succeeded, some failed - show warning with details
          const errorData = {
              title: 'Partial Import',
              message: `Successfully imported ${importedCount} customer(s). ${skippedCount} customer(s) were skipped.`,
              errors: errors
          };
          setUploadSuccess(`Successfully imported ${importedCount} customer(s). ${skippedCount} customer(s) were skipped.`);
          setUploadError(JSON.stringify(errorData));
          // Don't close modal - let admin read errors and close manually
      } else {
          // All succeeded - no errors
          setUploadSuccess(`Successfully imported ${importedCount} customer(s)`);
      }
        
        await fetchCustomers();
        
        // Only auto-close modal if import was successful AND there are no errors
        // If there are errors, keep modal open so admin can read them
        if (importedCount > 0 && errors.length === 0) {
            setTimeout(() => {
                setShowAddCustomerModal(false);
                setExcelFile(null);
                setPreviewData([]);
                setShowPreview(false);
                setDefaultPassword('Trial123!');
            }, 2000); // Give admin time to see success message
        }
        // If there are errors, modal stays open - admin can close manually after reading

      } catch (error: any) {
        console.error('Error importing customers:', error);
        // Try to parse error message to see if it's structured
        const errorMessage = error?.message || 'Failed to import customers. Please try again.';
        
        // Check if it's a structured error (starts with "Import failed" or similar)
        if (errorMessage.includes('Import failed') || errorMessage.includes('Errors:')) {
          // Parse the error into structured format
          const lines = errorMessage.split('\n');
          const title = lines[0] || 'Import Failed';
          const message = lines[2] || 'No customers were imported.';
          const errorLines = lines.slice(4).filter((line: string) => line.trim());
          
          const errors = errorLines.map((line: string) => {
            const match = line.match(/^(.+?):\s*(.+)$/);
            if (match) {
              return { email: match[1].trim(), error: match[2].trim() };
            }
            return { email: 'Unknown', error: line.trim() };
          });
          
          const errorData = {
            title: title,
            message: message,
            errors: errors
          };
          setUploadError(JSON.stringify(errorData));
        } else {
          setUploadError(errorMessage);
        }
        setUploadSuccess(null); // Clear any success message
        // Don't close modal on error - let admin read the error and close manually
    } finally {
        setUploading(false);
    }
  };

  const standardizePhoneNumber = (phone: string): string => {
    if (!phone) return '';
    
    // Convert to string first, then strip - handles both string and numeric values
    let cleaned = String(phone).trim();
    
    // Clean up phone number - remove any decimal points from Excel conversion
    if (cleaned.endsWith('.0')) {
      cleaned = cleaned.slice(0, -2);
    }
    
    // Remove all non-digit characters except +
    cleaned = cleaned.replace(/[^\d+]/g, '');
    
    // Handle Malaysian phone numbers
    if (cleaned.startsWith('0')) {
      // Replace leading 0 with +60 (e.g., 0123456789 -> +60123456789)
      cleaned = '+60' + cleaned.substring(1);
    } else if (cleaned.startsWith('60') && !cleaned.startsWith('+60')) {
      // Add + prefix if missing (e.g., 60123456789 -> +60123456789)
      cleaned = '+' + cleaned;
    } else if (!cleaned.startsWith('+')) {
      // If doesn't start with +60, add it (assuming Malaysian number)
      cleaned = '+60' + cleaned;
    }
    
    return cleaned;
  };
  
  // Excel copy/paste often smuggles in invisible characters (zero-width space, word joiner,
  // BOM, non-breaking spaces). These survive String.trim() and silently break email/auth
  // validation on the server. Strip them and normalize odd spaces to a plain space.
  const sanitizeCell = (value: any): string => {
    return String(value ?? '')
      .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')            // zero-width & word joiner & BOM
      .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, ' ') // exotic spaces → normal space
      .trim();
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return false;
    const normalized = email.toLowerCase().trim();
    // Standard single-address check: no spaces/@ in local or domain, and a dotted domain.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
  };
  
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return false;
    const standardized = standardizePhoneNumber(phone);
    return standardized.startsWith('+60') && standardized.length >= 12;
  };

  const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
        <div className="page-header">
          <div className="page-header-left">
          <div className="text-sm text-gray-500 mb-1">Admin Only</div>
          <h1 className="page-title">Customers</h1>
          </div>
          <div className="page-header-right">
            <button 
              onClick={() => navigate('/admin/today-filings')}
              className="f-btn f-btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Today Filings</span>
            </button>
            <button
              onClick={() => navigate('/admin/announcements')}
              className="f-btn f-btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              <span>Announcements</span>
            </button>
            <button
              onClick={() => navigate('/admin/msic-codes')}
              className="f-btn f-btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <span>MSIC Codes</span>
            </button>
            <button 
              onClick={handleAddCustomer}
              className="f-btn f-btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Add Customer</span>
            </button>
            <button 
              onClick={handleExportExcel}
              className="f-btn f-btn-success"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export Excel</span>
            </button>
          </div>
          </div>
  
          {/* Filing Card - Wraps search, filters, table, pagination */}
          <div className="page-card">
            {/* Country Tabs */}
            <div className="page-tabs mb-6">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => {
                    setSelectedCountry('MY');
                    setCurrentPage(1);
                  }}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      selectedCountry === 'MY'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  Malaysia (MY)
                </button>
                <button
                  onClick={() => {
                    setSelectedCountry('SG');
                    setCurrentPage(1);
                  }}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      selectedCountry === 'SG'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  Singapore (SG)
                </button>
              </nav>
            </div>

            {/* Search */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by Company Name, Email, or Phone Number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          
  
            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr>
                    <th className="page-table-th">Company Name</th>
                    <th className="page-table-th">Owner Contact</th>
                    <th className="page-table-th">Created At</th>
                    <th className="page-table-th">Credit Balance</th>
                    <th className="page-table-th">Referred By</th>
                    <th className="page-table-th">Last Filing</th>
                    <th className="page-table-th">Actions</th>
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="page-table-td">
                        Loading...
                      </td>
                    </tr>  
                  ) : (
                    currentCustomers.map((customer) => (
                      <tr key={customer.id}>
                        {/* Remove className from tr - let CSS handle alternating colors */}
                        <td className="page-table-td">{customer.company_name}</td>
                        <td className="page-table-td">
                          <div className="flex flex-col">
                            <span className="text-gray-900">{customer.email || '-'}</span>
                            <span className="text-gray-500 text-xs mt-0.5">{customer.phone_number || '-'}</span>
                          </div>
                        </td>
                        <td className="page-table-td">
                          {new Date(customer.created_at).toLocaleDateString('en-GB')}
                        </td>
                        <td className="page-table-td">
                          {selectedCountry === 'SG' ? (
                            <div>
                              <div>Credit: {customer.credit_balance}</div>
                              <div className="text-sm text-gray-500">
                                {customer.currency}: {customer.currency_balance || 0}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="flex flex-col text-center">
                                {/* <span>{customer.currency}</span> */}
                                <span>{customer.credit_balance}</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="page-table-td">{customer.referred_by || '-'}</td>
                        <td className="page-table-td">
                          {customer.last_filing ? new Date(customer.last_filing).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="page-table-td">
                          <div className="flex justify-center space-x-2">
                            <button 
                              onClick={() => handleEdit(customer.id)}
                              className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleViewFilings(customer.id)}
                              className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap"
                            >
                              View Filings
                            </button>
                            <button 
                              onClick={() => handleViewUsage(customer.id)}
                              className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap"
                            >
                              View Usage
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
  
            {/* Pagination */}
            {!loading && filteredCustomers.length > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                <div className="flex items-center space-x-2">
                  <span>Rows per page:</span>
                  <select 
                    value={rowsPerPage} 
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span>
                    {startIndex + 1}-{Math.min(endIndex, filteredCustomers.length)} of {filteredCustomers.length}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`f-btn f-btn-soft f-btn-xs ${
                          currentPage === pageNum 
                            ? 'f-btn-primary' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Add Customers</h3>
              </div>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setExcelFile(null);
                  setUploadError(null);
                  setUploadSuccess(null);
                  setPreviewData([]);
                  setShowPreview(false);
                  setDefaultPassword('Trial123!');
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Excel File Upload */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Excel File</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 
                      file:mr-4 file:py-3 file:px-6 
                      file:rounded-lg file:border-0 
                      file:text-sm file:font-semibold 
                      file:bg-blue-50 file:text-blue-700 
                      hover:file:bg-blue-100 
                      file:cursor-pointer
                      file:transition-colors
                      border border-gray-300 rounded-lg p-2
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {excelFile && (
                    <div className="mt-2 flex items-center space-x-2 text-sm text-green-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{excelFile.name}</span>
                    </div>
                  )}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-blue-800">
                    <strong>Required columns:</strong> <code className="bg-white px-1.5 py-0.5 rounded">company_name</code>, <code className="bg-white px-1.5 py-0.5 rounded">email</code>, <code className="bg-white px-1.5 py-0.5 rounded">phone_number</code>, <code className="bg-white px-1.5 py-0.5 rounded">country_iso</code>, <code className="bg-white px-1.5 py-0.5 rounded">company_address</code>
                  </p>
                </div>
                {/* Developer Notice for Phone Number */}
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3 mt-2" style={{ borderColor: 'var(--warning)' }}>
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-xs mt-1 text-amber-800">
                        Phone numbers must include the country code prefix <strong>'+60'</strong> (e.g., <code className="bg-white px-1.5 py-0.5 rounded text-xs">+60123456789</code>)
                      </p>
                      <p className="text-xs mt-1 text-amber-800">
                        Country iso must be in the format <strong>'MY'</strong> (e.g., <code className="bg-white px-1.5 py-0.5 rounded text-xs">MY, SG, GB</code>)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Default Password */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Default Password</span>
                </label>
                <input
                  type="text"
                  value={defaultPassword}
                  onChange={(e) => setDefaultPassword(e.target.value)}
                  className="block w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-3 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    transition-all"
                  placeholder="Enter default password"
                />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> This password will be used for all imported customer accounts. Each account will receive <strong className="text-amber-900">2 trial credits</strong>.
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {uploadError && (() => {
                // Try to parse as structured error, fallback to plain text
                let errorContent: any = null;
                try {
                  const parsed = JSON.parse(uploadError);
                  if (parsed.title && parsed.errors) {
                    errorContent = parsed;
                  }
                } catch {
                  // Not JSON, treat as plain text
                }

                if (errorContent) {
                  // Structured error display
                  return (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-red-800 mb-1">{errorContent.title}</h4>
                          <p className="text-sm text-red-700 mb-3">{errorContent.message}</p>
                          <div className="bg-white rounded-md border border-red-200 p-3 max-h-64 overflow-y-auto">
                            <p className="text-xs font-semibold text-red-800 mb-2">Error Details:</p>
                            <ul className="space-y-2">
                              {errorContent.errors.slice(0, 10).map((err: any, index: number) => (
                                <li key={index} className="flex items-start space-x-2">
                                  <span className="text-red-500 mt-1">•</span>
                                  <div className="flex-1">
                                    <span className="text-xs font-medium text-red-900">{err.email}:</span>
                                    <span className="text-xs text-red-700 ml-1">{err.error}</span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                            {errorContent.errors.length > 10 && (
                              <p className="text-xs text-red-600 mt-2 italic">
                                ... and {errorContent.errors.length - 10} more error(s)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Plain text error display
                  return (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg flex items-start space-x-3">
                      <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium whitespace-pre-line">{uploadError}</p>
                    </div>
                  );
                }
              })()}

              {/* Success Message */}
              {uploadSuccess && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-lg flex items-start space-x-3">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-medium">{uploadSuccess}</p>
                </div>
              )}

              {/* Preview Table */}
              {showPreview && previewData.length > 0 && (
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>Preview ({previewData.length} customers)</span>
                    </h4>
                  </div>
                  <div className="overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Country</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Address</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.slice(0, 10).map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.company_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.email}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.phone_number || <span className="text-gray-400">-</span>}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {row.country_iso || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.company_address || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewData.length > 10 && (
                      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 text-center">
                          Showing first 10 of {previewData.length} customers
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Action Buttons */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setExcelFile(null);
                  setUploadError(null);
                  setUploadSuccess(null);
                  setPreviewData([]);
                  setShowPreview(false);
                  setDefaultPassword('Trial123!');
                }}
                className="f-btn f-btn-soft"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handlePreview}
                className="f-btn f-btn-primary"
                disabled={!excelFile || uploading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>Preview</span>
              </button>
              <button
                onClick={handleImport}
                className="f-btn f-btn-success"
                disabled={!showPreview || previewData.length === 0 || uploading}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span>Import</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </MainLayout>
    );
  }
